import { useState } from "react";
import type { JobOperationDto, WorkCenterDto } from "@grizzly/shared";
import { api, ApiClientError } from "../../lib/apiClient.js";
import { operationStatus } from "@grizzly/shared";
import { Badge } from "../ui/badge.js";
import { Button } from "../ui/button.js";
import { SelectInput, TextInput } from "../ui/field.js";
import { useToast } from "../../lib/useToast.js";
import { useI18n, type MessageKey } from "../../i18n/index.js";
import { Plus } from "lucide-react";

const EMPTY_FORM = {
  workCenterId: "",
  description: "",
  setupMinutes: "0",
  runMinutesPerUnit: "0",
};

export function OperationPanel({
  jobId,
  operations,
  workCenters,
  canEdit,
  onChanged,
}: {
  jobId: string;
  operations: JobOperationDto[];
  workCenters: WorkCenterDto[];
  canEdit: boolean;
  onChanged(): void;
}) {
  const { push } = useToast();
  const { t } = useI18n();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.addJobOperation(jobId, {
        workCenterId: form.workCenterId || "",
        description: form.description,
        setupMinutes: Number(form.setupMinutes || "0"),
        runMinutesPerUnit: Number(form.runMinutesPerUnit || "0"),
      });
      push(t("jobs.operations.addedToast"));
      setForm(EMPTY_FORM);
      setAdding(false);
      onChanged();
    } catch (err) {
      if (err instanceof ApiClientError) push(err.message, undefined, "danger");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-ink">{t("jobs.operations.title")}</h2>
        {canEdit ? (
          <Button size="sm" variant="secondary" onClick={() => setAdding((v) => !v)}>
            <Plus className="h-4 w-4" aria-hidden="true" /> {t("common.add")}
          </Button>
        ) : null}
      </div>
      {operations.length === 0 ? (
        <p className="text-sm text-ink-muted">{t("jobs.operations.empty")}</p>
      ) : (
        <ol className="space-y-2">
          {operations.map((op) => (
            <li
              key={op.id}
              className="flex items-center justify-between gap-2 rounded border border-line px-3 py-2"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-ink">
                  {op.sequence + 1}. {op.description}
                </p>
                <p className="text-xs text-ink-muted">
                  {op.workCenterName ?? t("jobs.operations.noWorkCenter")} ·{" "}
                  {t("jobs.operations.setup", { minutes: op.setupMinutes })} ·{" "}
                  {t("jobs.operations.run", { minutes: op.runMinutesPerUnit })}
                </p>
              </div>
              <Badge tone={operationStatus(op.status).tone}>
                {t(`status.operation.${op.status}` as MessageKey)}
              </Badge>
            </li>
          ))}
        </ol>
      )}
      {adding ? (
        <div className="mt-3 space-y-2 border-t border-line pt-3">
          <SelectInput
            label={t("common.fields.workCenter")}
            options={[
              { value: "", label: t("common.none") },
              ...workCenters.map((w) => ({ value: w.id, label: w.name })),
            ]}
            value={form.workCenterId}
            onChange={(e) => setForm((f) => ({ ...f, workCenterId: e.target.value }))}
          />
          <TextInput
            label={t("jobs.operations.operation")}
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          />
          <div className="grid gap-2 sm:grid-cols-2">
            <TextInput
              label={t("common.fields.setupMinutes")}
              value={form.setupMinutes}
              onChange={(e) => setForm((f) => ({ ...f, setupMinutes: e.target.value }))}
            />
            <TextInput
              label={t("common.fields.runMinutes")}
              value={form.runMinutesPerUnit}
              onChange={(e) =>
                setForm((f) => ({ ...f, runMinutesPerUnit: e.target.value }))
              }
            />
          </div>
          <div className="flex gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={submit}
              disabled={busy || !form.description.trim()}
            >
              {t("common.save")}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setAdding(false)}>
              {t("common.cancel")}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
